import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { api } from '../services/api';
import { perfMonitor } from '../utils/performanceMonitor';
import * as Sentry from '@sentry/react';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, phone?: string) => Promise<{needsVerification: boolean, email: string}>;
  verifyEmailCode: (email: string, token: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (updates: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Track whether we've had an initial login to distinguish from token refresh
  const hasHadInitialLogin = useRef(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        hasHadInitialLogin.current = true; // Mark that we have a user session
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 Auth event:', event, 'Has session:', !!session, 'User email:', session?.user?.email);

      // Only set loading for user-initiated auth changes, NOT automatic token refresh
      // SIGNED_IN fires both on initial login AND on auto token refresh
      // Use ref instead of state to avoid stale closure issues
      const isInitialLogin = event === 'SIGNED_IN' && !hasHadInitialLogin.current;
      const shouldShowLoading = isInitialLogin || event === 'SIGNED_OUT' || event === 'PASSWORD_RECOVERY';

      // Mark that we've had an initial login
      if (isInitialLogin && session?.user) {
        hasHadInitialLogin.current = true;
      }

      // Reset flag on sign out
      if (event === 'SIGNED_OUT') {
        hasHadInitialLogin.current = false;
      }

      if (shouldShowLoading) {
        console.log('🔄 Setting loading state for auth event:', event);
        setLoading(true);
      } else {
        console.log('⚡ Skipping loading state for background auth event:', event);
      }

      try {
        // Handle password recovery specifically
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery detected, redirecting to reset password page');
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false); // Reset loading before early return
          // Redirect to reset password page
          navigate('/reset-password');
          return;
        }
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // User logged in - set up user data
          try {
            console.log('🔐 Fetching user data for:', session.user.email);
            // Get user from your existing users table
            let existingUser = await perfMonitor.trackAPI('getUserByEmail', () => api.getUserByEmail(session.user.email!));

            // If user doesn't exist in database (new OAuth user), create them
            if (!existingUser) {
              console.log('🆕 New OAuth user detected, syncing to database:', session.user.email);

              const syncResult = await api.syncOAuthUser({
                email: session.user.email!,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name,
                auth_id: session.user.id,
                provider: session.user.app_metadata?.provider,
                phone: session.user.user_metadata?.phone
              });

              if (syncResult) {
                existingUser = syncResult.user;
                console.log(`✅ ${syncResult.created ? 'Created' : 'Linked'} OAuth user in database`);
              } else {
                console.error('❌ Failed to sync OAuth user to database');
                // Continue with session user data as fallback
              }
            }

            if (existingUser) {
              // SECURITY: Validate that Supabase user ID matches database auth_id
              // This prevents session hijacking if database records are compromised
              if (existingUser.auth_id && existingUser.auth_id !== session.user.id) {
                console.error('🚨 SECURITY: Session user ID mismatch!');
                console.error('Supabase ID:', session.user.id);
                console.error('Database ID:', existingUser.auth_id);

                // Sign out immediately - potential security issue
                await supabase.auth.signOut();
                localStorage.removeItem('user');
                window.dispatchEvent(new Event('userStateChanged'));
                setLoading(false); // Reset loading before early return
                return;
              }

              // Store ONLY essential fields in localStorage to avoid quota issues
              // Large fields (social_links, other_sports, bio) can be fetched when needed
              const userData = {
                id: existingUser.id,
                email: existingUser.email,
                name: existingUser.name,
                role: existingUser.role,
                profile_picture: existingUser.profile_picture,
                specialty_sport: existingUser.specialty_sport
              };

              // Only update and dispatch event if data has changed
              const currentUserData = localStorage.getItem('user');
              const currentUser = currentUserData ? JSON.parse(currentUserData) : null;

              if (!currentUser || currentUser.id !== userData.id || currentUser.role !== userData.role) {
                try {
                  localStorage.setItem('user', JSON.stringify(userData));
                  // Dispatch event to update UI components
                  window.dispatchEvent(new Event('userStateChanged'));
                } catch (storageError: any) {
                  if (storageError.name === 'QuotaExceededError') {
                    console.error('🚨 localStorage quota exceeded! Clearing old data and retrying...');
                    // Clear all non-essential items from localStorage
                    Object.keys(localStorage).forEach(key => {
                      if (!key.startsWith('sb-') && key !== 'user') {
                        localStorage.removeItem(key);
                      }
                    });
                    // Retry storing user data
                    localStorage.setItem('user', JSON.stringify(userData));
                    window.dispatchEvent(new Event('userStateChanged'));
                  } else {
                    throw storageError;
                  }
                }
              }

              // Set Sentry user context for error tracking
              Sentry.setUser({
                id: userData.id,
                email: userData.email,
                username: userData.name,
                role: userData.role,
              });
            } else {
              // Fallback: Use session data if database user creation failed
              console.warn('⚠️  Using session data as fallback for user');
              const fallbackUserData = {
                id: session.user.id, // Use Supabase auth ID as fallback
                email: session.user.email,
                name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || 'User',
                role: 'customer'
              };
              localStorage.setItem('user', JSON.stringify(fallbackUserData));
              window.dispatchEvent(new Event('userStateChanged'));
            }
          } catch (error) {
            // Silent error handling
            console.error('Error setting up user data:', error);
          }
        } else {
          // User logged out - clean up
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('userStateChanged'));

          // Clear Sentry user context
          Sentry.setUser(null);
        }
      } finally {
        // Only reset loading if we set it (for sign-in/sign-out events)
        if (shouldShowLoading) {
          console.log('✅ Auth state change processing complete, setting loading to false');
          setLoading(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    
    // The onAuthStateChange listener will handle updating state
  };

  const signUp = async (email: string, password: string, name: string, phone?: string): Promise<{needsVerification: boolean, email: string}> => {
    console.log('🚀 Starting signup process for:', email);

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          phone,
          role: 'customer', // Store role in metadata
        },
      },
    });

    console.log('📧 Signup response:', { data, error });

    if (error) {
      console.error('❌ Signup error:', error);
      throw error;
    }

    // SECURITY: Check if email already exists
    // When email exists, Supabase returns user object but with empty identities array
    if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
      console.error('❌ Account already exists for:', email);
      throw new Error('User already registered');
    }

    // SECURITY: Validate that a session was actually created
    if (!data.session) {
      console.error('❌ No session created for:', email);
      throw new Error('Failed to create account. Please try again.');
    }

    // Instant login - no email verification required
    // The onAuthStateChange listener will handle updating state
    const result = {
      needsVerification: false, // Instant login enabled
      email: email
    };

    console.log('✅ Signup successful - instant login enabled:', result);
    return result;
  };

  const verifyEmailCode = async (email: string, token: string): Promise<void> => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });

    if (error) throw error;

    // After successful verification, the user session will be available
    if (data.session && data.user) {
      setSession(data.session);
      setUser(data.user);

      // Store user data in localStorage for backward compatibility
      try {
        const existingUser = await perfMonitor.trackAPI('getUserByEmail', () => api.getUserByEmail(data.user!.email!));
        if (existingUser) {
          // Store ONLY essential fields in localStorage to avoid quota issues
          const userData = {
            id: existingUser.id,
            email: existingUser.email,
            name: existingUser.name,
            role: existingUser.role,
            profile_picture: existingUser.profile_picture,
            specialty_sport: existingUser.specialty_sport
          };
          try {
            localStorage.setItem('user', JSON.stringify(userData));
          } catch (storageError: any) {
            if (storageError.name === 'QuotaExceededError') {
              console.error('🚨 localStorage quota exceeded during verification! Clearing old data...');
              Object.keys(localStorage).forEach(key => {
                if (!key.startsWith('sb-') && key !== 'user') {
                  localStorage.removeItem(key);
                }
              });
              localStorage.setItem('user', JSON.stringify(userData));
            } else {
              throw storageError;
            }
          }
        } else {
          // Fallback if user not found in database yet
          localStorage.setItem('user', JSON.stringify({
            id: data.user.id, // Use Supabase auth ID as fallback
            email: data.user.email,
            name: data.user.user_metadata?.name || '',
            role: 'customer',
          }));
        }
        window.dispatchEvent(new Event('userStateChanged'));
      } catch (error) {
        // Fallback storage
        localStorage.setItem('user', JSON.stringify({
          id: data.user.id, // Use Supabase auth ID as fallback
          email: data.user.email,
          name: data.user.user_metadata?.name || '',
          role: 'customer',
        }));
        window.dispatchEvent(new Event('userStateChanged'));
      }
    }
  };

  const resendVerificationCode = async (email: string): Promise<void> => {
    console.log('🔄 Resending verification code to:', email);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/verify-email`,
      }
    });

    console.log('📬 Resend response:', { error });

    if (error) {
      console.error('❌ Resend error:', error);
      throw error;
    }

    console.log('✅ Verification code resent successfully');
  };

  const signOut = async () => {
    try {
      // Try to sign out from Supabase (may fail with 403 if session is invalid)
      await supabase.auth.signOut();
    } catch (error: any) {
      // Ignore all errors - we'll force clear everything anyway
      console.log('Supabase signOut error (will force clear):', error?.message || error);
    }

    // Force clear ALL Supabase auth data from localStorage
    // Supabase stores session data with keys starting with 'sb-'
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key === 'user') {
        localStorage.removeItem(key);
      }
    });

    // Force set user and session to null immediately to prevent re-login
    setUser(null);
    setSession(null);

    window.dispatchEvent(new Event('userStateChanged'));

    // Force navigation to auth page
    navigate('/auth', { replace: true });
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  };

  const updateProfile = async (updates: any) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase.auth.updateUser({
      data: updates,
    });

    if (error) throw error;
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    verifyEmailCode,
    resendVerificationCode,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};